import { FLOOR_HEIGHT_M } from "@uzverse/world-scene";
import { isStyled, parseStyle } from "./brand.js";
import type { BuildingStyle } from "./brand.js";

/**
 * Pure generators of the world client: `(params) -> parts`. No three.js here, so part tables
 * are unit tested directly. Units are meters, Y up; +X east, -Z north (docs/PHASE2_PLAN.md 2.2).
 */

export type Vec3 = [number, number, number];
export type Side = "n" | "e" | "s" | "w";
export const SIDES: readonly Side[] = ["n", "s", "e", "w"];

/** One axis-aligned box of a generated model. */
export interface BoxPart {
  /** The part id picking and `world.setMaterial` use (`f2.wall.n`, `f0.slab`, `roof`). */
  id: string;
  /**
   * `surface` parts take node materials; `window` bands, the door and `detail` pieces (parapet,
   * rooftop units, entrance canopy, sign) are decoration that picks as the surface they sit on.
   */
  role: "surface" | "window" | "door" | "detail";
  kind: "slab" | "wall" | "roof" | "body" | "canopy" | "sign" | "plinth" | "pier";
  /** A facade look that overrides the node's material for this part (styled buildings). */
  look?: "curtain" | "stone" | "brick" | "metal";
  level?: number;
  side?: Side;
  center: Vec3;
  size: Vec3;
  /**
   * Where the windows of this wall (or window band) run, along local X or Z: the facade shader
   * cuts the glazing cells from this, so walls and bands agree on where the windows are.
   */
  win?: { axis: "x" | "z"; center: number; length: number; y?: number };
}

export interface BuildingBox {
  width: number;
  depth: number;
  /** Floor levels drawn above ground, lowest first. */
  levels: number[];
  /** Total height in meters. */
  height: number;
  /** Slabs first, then walls, then windows, then the roof (so equal materials form runs). */
  parts: BoxPart[];
}

const WALL_T = 0.3;
const SLAB_T = 0.3;
const ROOF_T = 0.4;
const WINDOW_T = 0.08;
/** Height of a window band and its sill above the floor surface (shared with the facade shader). */
export const WINDOW_H = 1.8;
const MAX_LEVELS = 200;
const DOOR_W = 2.2;
const DOOR_H = 3.1;
const DOOR_T = 0.14;
/** Depth and half-width of the trigger volume in front of a door (meters). */
const DOOR_TRIGGER_DEPTH = 1.6;
const DOOR_TRIGGER_HALF_W = 1.8;

const finite = (v: unknown, fallback: number): number =>
  typeof v === "number" && Number.isFinite(v) && v > 0 ? v : fallback;

/**
 * The floors of a `building.box`: a count (the seeded landmark: `floors: 12`) or an array of
 * `{ level }` (company placements: `floors: [{ level: 0, height: 4 }]`). Basements (level < 0)
 * are not drawn above ground; nothing usable gives a single ground floor.
 */
export function floorLevels(floors: unknown): number[] {
  let levels: number[] = [];
  if (typeof floors === "number" && Number.isFinite(floors)) {
    levels = Array.from(
      { length: Math.max(0, Math.min(MAX_LEVELS, Math.floor(floors))) },
      (_, i) => i,
    );
  } else if (Array.isArray(floors)) {
    levels = floors
      .map((f: unknown): unknown =>
        typeof f === "object" && f !== null ? (f as { level?: unknown }).level : f,
      )
      .filter((l): l is number => typeof l === "number" && Number.isInteger(l) && l >= 0)
      .slice(0, MAX_LEVELS);
  }
  const unique = [...new Set(levels)].sort((a, b) => a - b);
  return unique.length > 0 ? unique : [0];
}

export interface BuildingBoxOptions {
  /** Draw window bands (the low quality tier leaves them out). Default true. */
  windows?: boolean;
}

/** Outward unit direction (x, z) of each side in the building's local frame. */
export const SIDE_NORMAL: Readonly<Record<Side, readonly [number, number]>> = {
  n: [0, -1],
  s: [0, 1],
  e: [1, 0],
  w: [-1, 0],
};

const isSide = (v: unknown): v is Side => v === "n" || v === "s" || v === "e" || v === "w";

/**
 * The ground-floor door: on the `doorSide` param (default south, the +Z side; rotating the
 * building in blueprint mode turns the door toward another road). A door is decoration on the
 * wall plus a trigger volume in front of it, both in the building's local frame.
 */
export interface DoorSpec {
  side: Side;
  /** Door center at the wall's outer face, on the ground (x, z). */
  position: [number, number];
  /** Outward direction (x, z). */
  normal: [number, number];
  /** Trigger volume in front of the door: center and half extents along local x and z. */
  trigger: { center: [number, number]; half: [number, number] };
  /** Where the visitor stands just inside the door (x, z). */
  inside: [number, number];
}

export function doorOf(params: Record<string, unknown>): DoorSpec {
  const width = finite(params["width"], 20);
  const depth = finite(params["depth"], 20);
  const side: Side = isSide(params["doorSide"]) ? params["doorSide"] : "s";
  const [nx, nz] = SIDE_NORMAL[side];
  const reach = side === "n" || side === "s" ? depth / 2 : width / 2;
  const half: [number, number] =
    nx === 0
      ? [DOOR_TRIGGER_HALF_W, DOOR_TRIGGER_DEPTH / 2]
      : [DOOR_TRIGGER_DEPTH / 2, DOOR_TRIGGER_HALF_W];
  return {
    side,
    position: [nx * reach, nz * reach],
    normal: [nx, nz],
    trigger: {
      center: [nx * (reach + DOOR_TRIGGER_DEPTH / 2), nz * (reach + DOOR_TRIGGER_DEPTH / 2)],
      half,
    },
    inside: [nx * (reach - WALL_T - 1.2), nz * (reach - WALL_T - 1.2)],
  };
}

/** The glazing run of a wall side from its window band size (north/south run along X). */
const winOf = (side: Side, size: Vec3): NonNullable<BoxPart["win"]> =>
  side === "n" || side === "s"
    ? { axis: "x", center: 0, length: size[0] }
    : { axis: "z", center: 0, length: size[2] };

/** Parts of the `building.box` generator: slab and four walls per floor, windows, door, roof. */
/** Does this building draw as a glass curtain wall (the plaza and tower presets)? */
export function isCurtain(params: Record<string, unknown>): boolean {
  if (!isStyled(params)) return false;
  const p = parseStyle(params["style"]).preset;
  return p === "plaza" || p === "tower" || p === "showroom";
}

export function buildingBox(
  params: Record<string, unknown>,
  options: BuildingBoxOptions = {},
): BuildingBox {
  const styled = isStyled(params);
  const style = parseStyle(params["style"]);
  const curtain = isCurtain(params);
  const wallLook: BoxPart["look"] | undefined = curtain
    ? "curtain"
    : styled && style.preset === "campus"
      ? "brick"
      : undefined;
  const width = finite(params["width"], 20);
  const depth = finite(params["depth"], 20);
  const levels = floorLevels(params["floors"]);
  const top = (levels[levels.length - 1]! + 1) * FLOOR_HEIGHT_M;
  const slabs: BoxPart[] = [];
  const walls: BoxPart[] = [];
  const windows: BoxPart[] = [];
  for (const level of levels) {
    const base = level * FLOOR_HEIGHT_M;
    slabs.push({
      id: `f${level}.slab`,
      role: "surface",
      kind: "slab",
      level,
      center: [0, base + SLAB_T / 2, 0],
      size: [width + 0.12, SLAB_T, depth + 0.12],
    });
    const wallH = FLOOR_HEIGHT_M - SLAB_T;
    const y = base + SLAB_T + wallH / 2;
    const sides: Record<Side, { center: Vec3; size: Vec3; win: { center: Vec3; size: Vec3 } }> = {
      n: {
        center: [0, y, -depth / 2 + WALL_T / 2],
        size: [width, wallH, WALL_T],
        win: {
          center: [0, y, -depth / 2 - WINDOW_T / 2],
          size: [Math.max(1, width - 3), WINDOW_H, WINDOW_T],
        },
      },
      s: {
        center: [0, y, depth / 2 - WALL_T / 2],
        size: [width, wallH, WALL_T],
        win: {
          center: [0, y, depth / 2 + WINDOW_T / 2],
          size: [Math.max(1, width - 3), WINDOW_H, WINDOW_T],
        },
      },
      e: {
        center: [width / 2 - WALL_T / 2, y, 0],
        size: [WALL_T, wallH, Math.max(0.1, depth - 2 * WALL_T)],
        win: {
          center: [width / 2 + WINDOW_T / 2, y, 0],
          size: [WINDOW_T, WINDOW_H, Math.max(1, depth - 3)],
        },
      },
      w: {
        center: [-width / 2 + WALL_T / 2, y, 0],
        size: [WALL_T, wallH, Math.max(0.1, depth - 2 * WALL_T)],
        win: {
          center: [-width / 2 - WINDOW_T / 2, y, 0],
          size: [WINDOW_T, WINDOW_H, Math.max(1, depth - 3)],
        },
      },
    };
    for (const side of SIDES) {
      const s = sides[side];
      const id = `f${level}.wall.${side}`;
      walls.push({
        id,
        role: "surface",
        kind: "wall",
        level,
        side,
        center: s.center,
        size: s.size,
        win: winOf(side, s.win.size),
        ...(wallLook ? { look: wallLook } : {}),
      });
      windows.push({
        id,
        role: "window",
        kind: "wall",
        level,
        side,
        center: s.win.center,
        size: s.win.size,
        win: winOf(side, s.win.size),
      });
    }
  }
  const doors: BoxPart[] = [];
  const doorBox = (door: DoorSpec, lateral: number): BoxPart => {
    const [nx, nz] = door.normal;
    const lx = nz * lateral; // along the wall
    const lz = -nx * lateral;
    return {
      id: `f0.wall.${door.side}`,
      role: "door",
      kind: "wall",
      level: 0,
      side: door.side,
      center: [
        door.position[0] + (nx * DOOR_T) / 2 + lx,
        SLAB_T + DOOR_H / 2,
        door.position[1] + (nz * DOOR_T) / 2 + lz,
      ],
      size: nx === 0 ? [DOOR_W, DOOR_H, DOOR_T] : [DOOR_T, DOOR_H, DOOR_W],
    };
  };
  if (levels.includes(0) && styled && style.entrance !== "double") {
    // A revolving or sliding entrance is drawn as its own piece (`BuildingExtras`); the swing
    // doors on both sides of a revolving drum are boxes like any door.
    if (style.entrance === "revolving") {
      const door = doorOf(params);
      doors.push(doorBox(door, -REVOLVING_SIDE), doorBox(door, REVOLVING_SIDE));
    }
  } else if (levels.includes(0)) {
    const door = doorOf(params);
    const [nx, nz] = door.normal;
    doors.push({
      id: `f0.wall.${door.side}`,
      role: "door",
      kind: "wall",
      level: 0,
      side: door.side,
      center: [
        door.position[0] + (nx * DOOR_T) / 2,
        SLAB_T + DOOR_H / 2,
        door.position[1] + (nz * DOOR_T) / 2,
      ],
      size: nx === 0 ? [DOOR_W, DOOR_H, DOOR_T] : [DOOR_T, DOOR_H, DOOR_W],
    });
  }
  const roof: BoxPart = {
    id: "roof",
    role: "surface",
    kind: "roof",
    center: [0, top + ROOF_T / 2, 0],
    size: [width + 0.6, ROOF_T, depth + 0.6],
  };
  const details = buildingDetails(
    params,
    { width, depth, top: top + ROOF_T, levels, style: styled ? style : null, curtain },
    options,
  );
  return {
    width,
    depth,
    levels,
    height: top + ROOF_T,
    parts: [
      ...slabs,
      ...walls,
      ...(options.windows === false || curtain ? [] : windows),
      ...doors,
      roof,
      ...details,
    ],
  };
}

const PARAPET_H = 0.7;
const PARAPET_T = 0.3;
const CANOPY_W = 4.4;
const CANOPY_D = 1.9;
const CANOPY_T = 0.5;

/**
 * Decoration that makes a box read as a building: a parapet around the roof, rooftop units and a
 * stair head, and over the ground-floor door a canopy on two posts with a sign plate on its
 * front. Every piece is one box (picking maps faces to parts, 12 triangles each). The low tier
 * keeps the parapet and the canopy only.
 */
function buildingDetails(
  params: Record<string, unknown>,
  b: {
    width: number;
    depth: number;
    top: number;
    levels: number[];
    style: BuildingStyle | null;
    curtain: boolean;
  },
  options: BuildingBoxOptions,
): BoxPart[] {
  const out: BoxPart[] = [];
  const full = options.windows !== false;
  const { width, depth, top } = b;
  const roofW = width + 0.6;
  const roofD = depth + 0.6;
  const py = top + PARAPET_H / 2;
  const roofPart = (kind: BoxPart["kind"], center: Vec3, size: Vec3): BoxPart => ({
    id: "roof",
    role: "detail",
    kind,
    center,
    size,
  });
  out.push(
    roofPart("roof", [0, py, -roofD / 2 + PARAPET_T / 2], [roofW, PARAPET_H, PARAPET_T]),
    roofPart("roof", [0, py, roofD / 2 - PARAPET_T / 2], [roofW, PARAPET_H, PARAPET_T]),
    roofPart(
      "roof",
      [roofW / 2 - PARAPET_T / 2, py, 0],
      [PARAPET_T, PARAPET_H, Math.max(0.1, roofD - 2 * PARAPET_T)],
    ),
    roofPart(
      "roof",
      [-roofW / 2 + PARAPET_T / 2, py, 0],
      [PARAPET_T, PARAPET_H, Math.max(0.1, roofD - 2 * PARAPET_T)],
    ),
  );
  if (full && width >= 10 && depth >= 10) {
    // Rooftop units (deterministic from the size) and, on taller buildings, a stair head.
    out.push(
      roofPart("body", [-width * 0.24, top + 0.6, -depth * 0.2], [2.6, 1.2, 1.8]),
      roofPart("body", [width * 0.18, top + 0.45, depth * 0.22], [1.8, 0.9, 1.5]),
    );
    if (b.levels.length >= 3) {
      out.push(roofPart("body", [width * 0.25, top + 1.1, -depth * 0.22], [3.2, 2.2, 3.2]));
    }
  }
  if (b.style) out.push(...styledDetails(params, b, full));
  if (b.levels.includes(0) && (!b.style || (b.style.canopy && !b.curtain))) {
    const door = doorOf(params);
    const [nx, nz] = door.normal;
    const x = door.position[0] + nx * (CANOPY_D / 2);
    const z = door.position[1] + nz * (CANOPY_D / 2);
    const alongX = nx === 0;
    const y = SLAB_T + DOOR_H + 0.15 + CANOPY_T / 2;
    const id = `f0.wall.${door.side}`;
    const canopy: BoxPart = {
      id,
      role: "detail",
      kind: "canopy",
      level: 0,
      side: door.side,
      center: [x, y, z],
      size: alongX ? [CANOPY_W, CANOPY_T, CANOPY_D] : [CANOPY_D, CANOPY_T, CANOPY_W],
    };
    out.push(canopy);
    if (full) {
      for (const s of [-1, 1]) {
        const lateral = s * (CANOPY_W / 2 - 0.2);
        out.push({
          id,
          role: "detail",
          kind: "canopy",
          level: 0,
          side: door.side,
          center: [
            door.position[0] + nx * (CANOPY_D - 0.2) + (alongX ? lateral : 0),
            SLAB_T + (DOOR_H + 0.15) / 2,
            door.position[1] + nz * (CANOPY_D - 0.2) + (alongX ? 0 : lateral),
          ],
          size: [0.14, DOOR_H + 0.15, 0.14],
        });
      }
    }
    out.push({
      id,
      role: "detail",
      kind: "sign",
      level: 0,
      side: door.side,
      center: [
        door.position[0] + nx * (CANOPY_D + 0.03),
        y,
        door.position[1] + nz * (CANOPY_D + 0.03),
      ],
      size: alongX
        ? [CANOPY_W - 0.3, CANOPY_T - 0.12, 0.06]
        : [0.06, CANOPY_T - 0.12, CANOPY_W - 0.3],
      win: { axis: alongX ? "x" : "z", center: 0, length: CANOPY_W - 0.3, y },
    });
  }
  return out;
}

/** Lateral offset of the swing doors on both sides of a revolving drum (meters). */
export const REVOLVING_SIDE = 3.0;
/** Canopy of a plaza entrance: wide and thin, on slim columns. */
export const PLAZA_CANOPY = { w: 10, d: 3.6, t: 0.32, y: 4.35 };

/**
 * What makes a plaza a plaza: a stone plinth around the base, stone piers framing the glass at
 * the corners, a cornice at the roof, a cantilevered canopy with slim columns over the entrance and
 * a few stone steps. Every piece is one box like the rest of the building, so it stays merged.
 */
function styledDetails(
  params: Record<string, unknown>,
  b: {
    width: number;
    depth: number;
    top: number;
    levels: number[];
    style: BuildingStyle | null;
    curtain: boolean;
  },
  full: boolean,
): BoxPart[] {
  const out: BoxPart[] = [];
  const style = b.style!;
  const { width, depth, top } = b;
  const piece = (
    id: string,
    kind: BoxPart["kind"],
    look: BoxPart["look"],
    center: Vec3,
    size: Vec3,
    side?: Side,
  ): BoxPart => ({
    id,
    role: "detail",
    kind,
    look,
    ...(side ? { side } : {}),
    level: 0,
    center,
    size,
  });
  if (b.curtain) {
    // Stone plinth, 1 m high and a little proud of the glass, on all four sides.
    const ph = 1.0;
    const pt = 0.5;
    out.push(
      piece(
        "f0.wall.n",
        "plinth",
        "stone",
        [0, ph / 2, -depth / 2 - pt / 2 + 0.2],
        [width + 0.8, ph, pt],
        "n",
      ),
      piece(
        "f0.wall.s",
        "plinth",
        "stone",
        [0, ph / 2, depth / 2 + pt / 2 - 0.2],
        [width + 0.8, ph, pt],
        "s",
      ),
      piece(
        "f0.wall.e",
        "plinth",
        "stone",
        [width / 2 + pt / 2 - 0.2, ph / 2, 0],
        [pt, ph, depth - 0.2],
        "e",
      ),
      piece(
        "f0.wall.w",
        "plinth",
        "stone",
        [-width / 2 - pt / 2 + 0.2, ph / 2, 0],
        [pt, ph, depth - 0.2],
        "w",
      ),
    );
    // Corner piers, the full height.
    if (full) {
      const pw = 1.1;
      for (const sx of [-1, 1]) {
        for (const sz of [-1, 1]) {
          out.push(
            piece(
              "roof",
              "pier",
              "stone",
              [sx * (width / 2 - pw / 2 + 0.2), top / 2, sz * (depth / 2 - pw / 2 + 0.2)],
              [pw, top, pw],
            ),
          );
        }
      }
    }
    // The cornice under the parapet.
    out.push(piece("roof", "plinth", "stone", [0, top - 0.15, 0], [width + 1.0, 0.8, depth + 1.0]));
  }
  if (b.levels.includes(0) && style.canopy) {
    const door = doorOf(params);
    const [nx, nz] = door.normal;
    const alongX = nx === 0;
    const id = `f0.wall.${door.side}`;
    const C = b.curtain
      ? PLAZA_CANOPY
      : { w: CANOPY_W, d: CANOPY_D, t: CANOPY_T, y: SLAB_T + DOOR_H + 0.15 + CANOPY_T / 2 };
    const cx = door.position[0] + nx * (C.d / 2);
    const cz = door.position[1] + nz * (C.d / 2);
    out.push({
      id,
      role: "detail",
      kind: "canopy",
      level: 0,
      side: door.side,
      look: "metal",
      center: [cx, C.y, cz],
      size: alongX ? [C.w, C.t, C.d] : [C.d, C.t, C.w],
    });
    if (full) {
      for (const s of [-1, 1]) {
        const lateral = s * (C.w / 2 - 0.35);
        out.push({
          id,
          role: "detail",
          kind: "canopy",
          level: 0,
          side: door.side,
          look: "metal",
          center: [
            door.position[0] + nx * (C.d - 0.35) + (alongX ? lateral : 0),
            C.y / 2,
            door.position[1] + nz * (C.d - 0.35) + (alongX ? 0 : lateral),
          ],
          size: [0.16, C.y, 0.16],
        });
      }
      // Two stone steps up to the door.
      for (const [i, step] of [0.28, 0.14].entries()) {
        const reach = 0.5 + i * 0.5;
        const wdt = 5.6 - i * 0.4;
        out.push(
          piece(
            id,
            "plinth",
            "stone",
            [
              door.position[0] + nx * (reach / 2 + 0.2 + i * 0.5),
              step / 2,
              door.position[1] + nz * (reach / 2 + 0.2 + i * 0.5),
            ],
            alongX ? [wdt, step, reach] : [reach, step, wdt],
            door.side,
          ),
        );
      }
    }
  }
  return out;
}

/** Triangles per box (12): face index / 12 is the part index in `parts` order. */
export const TRIANGLES_PER_BOX = 12;

/** The part a raycast face belongs to (undefined outside the table). */
export function partAtFace(
  parts: readonly BoxPart[],
  faceIndex: number | null | undefined,
): BoxPart | undefined {
  if (faceIndex === undefined || faceIndex === null) return undefined;
  return parts[Math.floor(faceIndex / TRIANGLES_PER_BOX)];
}

/** The surface box of a part id (for highlights). */
export function surfacePart(parts: readonly BoxPart[], id: string): BoxPart | undefined {
  return parts.find((p) => p.id === id && p.role === "surface");
}

/** Does this node's geometry draw as a `building.box`? */
export const isBuildingBox = (generator: string): boolean => generator === "building.box";

/** Footprint box of any generator's params, for framing and labels (meters). */
export function footprintOf(
  generator: string,
  params: Record<string, unknown>,
): { width: number; depth: number; height: number } {
  if (generator === "building.box") {
    const b = buildingBox(params);
    return { width: b.width, depth: b.depth, height: b.height };
  }
  const width = finite(params["width"], finite(params["radius"], 1) * 2);
  const depth = finite(params["depth"], finite(params["length"], finite(params["radius"], 1) * 2));
  return { width, depth, height: finite(params["height"], 1) };
}
